import { Directive, ElementRef, HostListener, inject, input, signal } from '@angular/core';

/**
 * Makes a side drawer resizable by dragging its left edge.
 *
 * Put it on the drag handle inside the panel: the handle resizes the panel
 * it sits in, remembers the width per browser, and marks the panel with
 * `is-resizing` while a drag is in progress (styles hang off that).
 *
 * Dragging left widens the drawer, a double-click restores the stylesheet's
 * default width, and the arrow keys nudge it for keyboard users.
 */
@Directive({
  selector: '[appDrawerResize]',
  standalone: true,
  host: {
    role: 'separator',
    tabindex: '0',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize panel',
    title: 'Drag to resize. Double-click to reset.',
    '[attr.aria-valuenow]': 'width()'
  }
})
export class DrawerResizeDirective {
  /** Where the chosen width is remembered, e.g. 'form-selector-drawer-width'. */
  readonly storageKey = input.required<string>({ alias: 'appDrawerResize' });
  /** Narrowest the drawer may go. */
  readonly minWidth = input(720);
  /** Sliver of the page left showing, so it still reads as a drawer. */
  readonly edgeGap = input(24);

  private static readonly KEYBOARD_STEP = 32;

  private handle = inject<ElementRef<HTMLElement>>(ElementRef);
  private dragStart: { pointerX: number; width: number } | null = null;

  /** Width set by dragging; null means the width from the stylesheet. */
  readonly width = signal<number | null>(null);

  /** The drawer this handle resizes. */
  private get panel(): HTMLElement {
    return this.handle.nativeElement.parentElement!;
  }

  constructor() {
    // Apply the remembered width as soon as the drawer is created, before it
    // is painted, so it doesn't open at the default width and jump.
    queueMicrotask(() => {
      const stored = this.loadWidth();
      if (stored !== null) {
        this.applyWidth(stored);
      }
    });
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    this.dragStart = { pointerX: event.clientX, width: this.panel.getBoundingClientRect().width };
    this.panel.classList.add('is-resizing');
    this.handle.nativeElement.setPointerCapture?.(event.pointerId);
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const start = this.dragStart;
    if (!start) return;
    // The handle is on the drawer's left edge, so dragging left widens it.
    this.applyWidth(this.clamp(start.width + start.pointerX - event.clientX));
  }

  @HostListener('pointerup')
  @HostListener('pointercancel')
  onPointerUp(): void {
    if (!this.dragStart) return;
    this.dragStart = null;
    this.panel.classList.remove('is-resizing');
    this.saveWidth();
  }

  /** Double-click the edge to go back to the default width. */
  @HostListener('dblclick')
  onDoubleClick(): void {
    this.applyWidth(null);
    this.saveWidth();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const step = DrawerResizeDirective.KEYBOARD_STEP;
    const delta = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0;
    if (!delta) return;
    event.preventDefault();
    const current = this.width() ?? this.panel.getBoundingClientRect().width;
    this.applyWidth(this.clamp(current + delta));
    this.saveWidth();
  }

  private applyWidth(width: number | null): void {
    this.width.set(width);
    this.panel.style.width = width === null ? '' : `${width}px`;
  }

  private clamp(width: number): number {
    const max = Math.max(this.minWidth(), window.innerWidth - this.edgeGap());
    return Math.round(Math.min(max, Math.max(this.minWidth(), width)));
  }

  private loadWidth(): number | null {
    try {
      const stored = Number(localStorage.getItem(this.storageKey()));
      return stored >= this.minWidth() ? stored : null;
    } catch {
      return null;
    }
  }

  private saveWidth(): void {
    try {
      const width = this.width();
      if (width === null) {
        localStorage.removeItem(this.storageKey());
      } else {
        localStorage.setItem(this.storageKey(), String(width));
      }
    } catch {
      // Storage unavailable: the width just won't be remembered.
    }
  }
}
